import {
  isSessionFeatureEvent,
  isSessionTaskEvent,
  isSessionTaskProgressEvent,
  isSessionPhaseEvent,
  isSessionLifecycleEvent,
  isSessionErrorEvent,
  SessionEvent,
} from "@mycompbox/shared";
import { SessionEventItem } from "./SessionEventItem";
import { cn } from "@/lib/utils";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDown, ArrowUpDown, Search, X } from "lucide-react";

export interface SessionActivityStreamProps {
  events: SessionEvent[];
  className?: string;
}

type Filter = "all" | "lifecycle" | "phases" | "features" | "tasks" | "errors";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "phases", label: "Phases" },
  { value: "features", label: "Features" },
  { value: "tasks", label: "Tasks" },
  { value: "errors", label: "Errors" },
];

function applyFilter(events: SessionEvent[], filter: Filter): SessionEvent[] {
  if (filter === "all") return events;
  return events.filter((e) => {
    switch (filter) {
      case "lifecycle": return isSessionLifecycleEvent(e);
      case "phases": return isSessionPhaseEvent(e);
      case "features": return isSessionFeatureEvent(e);
      case "tasks": return isSessionTaskEvent(e) || isSessionTaskProgressEvent(e);
      case "errors": return isSessionErrorEvent(e);
      default: return true;
    }
  });
}

function applySearch(events: SessionEvent[], q: string): SessionEvent[] {
  if (!q.trim()) return events;
  const lower = q.toLowerCase();
  return events.filter((e) => {
    if (isSessionFeatureEvent(e) || isSessionTaskEvent(e)) {
      return (
        e.payload.title?.toLowerCase().includes(lower) ||
        e.payload.identifier?.toLowerCase().includes(lower)
      );
    }
    if (isSessionErrorEvent(e)) return e.payload.errorMessage?.toLowerCase().includes(lower);
    if (isSessionPhaseEvent(e)) return `phase ${e.payload.phaseNumber}`.includes(lower);
    return false;
  });
}

export function SessionActivityStream({ events, className }: SessionActivityStreamProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [showJump, setShowJump] = useState(false);
  const scrolling = useRef(false);

  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortAsc, setSortAsc] = useState(true);

  const filtered = useMemo(() => {
    let r = applyFilter(events, filter);
    r = applySearch(r, search);
    return [...r].sort((a, b) => {
      const diff = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      return sortAsc ? diff : -diff;
    });
  }, [events, filter, search, sortAsc]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (!atBottom && !scrolling.current) {
      scrolling.current = true;
      setPaused(true);
      setShowJump(true);
    } else if (atBottom && scrolling.current) {
      scrolling.current = false;
      setPaused(false);
      setShowJump(false);
    }
  }, []);

  useEffect(() => {
    if (scrolling.current) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [filtered.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    return () => el.removeEventListener("scroll", checkScroll);
  }, [checkScroll]);

  const jumpToLatest = () => {
    scrolling.current = false;
    setPaused(false);
    setShowJump(false);
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  return (
    <div className={cn("flex flex-col w-full min-h-0 flex-1", className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3">
        {/* Filter pills */}
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                filter === f.value
                  ? "bg-white/[0.08] text-foreground"
                  : "text-muted-foreground hover:text-muted-foreground hover:bg-white/[0.03]",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Right side: count + sort + search toggle */}
        <div className="flex items-center gap-2">
          {paused && (
            <span className="text-xs text-amber-400/70 font-medium">PAUSED</span>
          )}
          <span className="text-xs text-muted-foreground tabular-nums">
            {filtered.length} event{filtered.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={() => setSortAsc(!sortAsc)}
            title={sortAsc ? "Oldest first — click for newest first" : "Newest first — click for oldest first"}
            className={cn(
              "h-6 w-6 rounded flex items-center justify-center transition-colors",
              !sortAsc ? "bg-white/[0.08] text-foreground" : "text-muted-foreground hover:text-muted-foreground",
            )}
          >
            <ArrowUpDown className="h-3 w-3" />
          </button>
          <button
            onClick={() => { setSearchOpen(!searchOpen); if (searchOpen) setSearch(""); }}
            className={cn(
              "h-6 w-6 rounded flex items-center justify-center transition-colors",
              searchOpen ? "bg-white/[0.08] text-foreground" : "text-muted-foreground hover:text-muted-foreground",
            )}
          >
            {searchOpen ? <X className="h-3 w-3" /> : <Search className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* Search bar (collapsible) */}
      {searchOpen && (
        <div className="mb-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events…"
            autoFocus
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:border-white/[0.12] focus:ring-1 focus:ring-white/[0.12] transition-colors"
          />
        </div>
      )}

      {/* Timeline */}
      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollRef}
          className="overflow-y-auto h-full pr-1 scrollbar-thin"
        >
          {filtered.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-xs">
              No events match the current filter.
            </div>
          ) : (
            <div className="py-1">
              {filtered.map((event, i) => (
                <div
                  key={`${event.sessionId}-${event.timestamp}-${event.eventType}-${i}`}
                  ref={i === filtered.length - 1 ? bottomRef : undefined}
                >
                  <SessionEventItem event={event} isLast={i === filtered.length - 1} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Jump to latest */}
        {showJump && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
            <Button
              onClick={jumpToLatest}
              size="sm"
              className="h-7 rounded-full px-3 text-xs shadow-lg bg-white/[0.08] hover:bg-white/[0.12] text-foreground border border-white/[0.1] backdrop-blur-sm"
            >
              <ArrowDown className="h-3 w-3 mr-1" />
              Latest
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
